import React, { useEffect, useRef, useState } from 'react';
import { TrendingUp, MousePointer, DollarSign, Users } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { caseStudies } from '../data/mock';

const CaseStudies = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [counts, setCounts] = useState({});
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Count-up animation for numbers
  useEffect(() => {
    if (isVisible) {
      const duration = 2000;
      const steps = 60;
      const stepDuration = duration / steps;

      let currentStep = 0;
      const timer = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;

        const newCounts = {};
        caseStudies.forEach((study, studyIndex) => {
          study.results.forEach(result => {
            const key = `${studyIndex}-${result.metric}`;
            // Extract numeric value
            const numericValue = parseFloat(result.value.replace(/[^0-9.]/g, ''));
            if (!isNaN(numericValue)) {
              newCounts[key] = (numericValue * progress).toFixed(1);
            }
          });
        });

        setCounts(newCounts);

        if (currentStep >= steps) {
          clearInterval(timer);
        }
      }, stepDuration);

      return () => clearInterval(timer);
    }
  }, [isVisible]);

  const getIcon = (metric) => {
    if (metric.includes('Reach')) return TrendingUp;
    if (metric.includes('Click')) return MousePointer;
    if (metric.includes('Conversions') || metric.includes('ROI')) return DollarSign;
    return Users;
  };

  const formatValue = (value, studyIndex, metric) => {
    const key = `${studyIndex}-${metric}`;
    const countValue = counts[key];
    
    if (!countValue) return value;

    if (value.includes('M')) return `${countValue}M`;
    if (value.includes('K')) return `$${countValue}K`;
    if (value.includes('%')) return `${countValue}%`;
    if (value.includes('$')) return `$${countValue}`;
    return Math.floor(countValue).toLocaleString();
  };

  return (
    <section id="case-studies" ref={sectionRef} className="py-24 bg-gradient-to-b from-[#151515] to-[#0B0B0B]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 0.8s ease-out'
            }}
          >
            Proven Results That Drive ROI
          </h2>
          <p 
            className="text-lg text-white/70 max-w-2xl mx-auto"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 0.8s ease-out 0.2s'
            }}
          >
            Real campaigns, real numbers, real impact on brand growth
          </p>
        </div>

        {/* Case Studies Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {caseStudies.map((study, studyIndex) => (
            <Card
              key={studyIndex}
              className="bg-white/5 backdrop-blur-sm border-white/10 hover:border-[#2563EB]/50 transition-all duration-300 group overflow-hidden"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(50px)',
                transition: `all 0.8s ease-out ${0.2 + studyIndex * 0.15}s`
              }}
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={study.image}
                  alt={study.brand}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B] to-transparent" />
              </div>

              <CardContent className="p-6 space-y-4">
                {/* Brand & Campaign */}
                <div>
                  <h3 className="text-xl font-bold text-white mb-1">{study.brand}</h3>
                  <p className="text-sm text-[#2563EB]">{study.campaign}</p>
                </div>

                {/* Results Grid */}
                <div className="grid grid-cols-2 gap-3">
                  {study.results.map((result, index) => {
                    const Icon = getIcon(result.metric);
                    return (
                      <div
                        key={index}
                        className="bg-white/5 p-3 rounded-lg border border-white/10 hover:bg-white/10 transition-colors duration-200"
                      >
                        <Icon className="text-[#2563EB] mb-1" size={16} />
                        <div className="text-2xl font-bold text-white mb-1">
                          {formatValue(result.value, studyIndex, result.metric)}
                        </div>
                        <div className="text-xs text-white/60">{result.metric}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Description */}
                <p className="text-sm text-white/70 leading-relaxed">
                  {study.description}
                </p>
              </CardContent>

              {/* Hover Glow Effect */}
              <div 
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                style={{
                  boxShadow: '0 0 30px rgba(37, 99, 235, 0.3)'
                }}
              />
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
